/**
 * Deployment vocabulary (E4, SA-385, spec D22, §9.1): which starter pack
 * names the records staff see — the sidebar entry, the page title, the
 * "New …" button. A deployment setting, not an install: it changes at
 * once through `PUT /api/workflows/packs/vocabulary` and never publishes
 * a workflow, which is why it is its own small dialog beside the install
 * wizard rather than a step inside it.
 */
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui'
import { useSetVocabularyPack, useWorkflowPacks } from '@/hooks/useWorkflowPacks'

function localized(blob: Record<string, string>): string {
  return blob.en ?? Object.values(blob)[0] ?? ''
}

export function DeploymentVocabularyDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const packsQuery = useWorkflowPacks(open)
  const setVocabulary = useSetVocabularyPack()
  const packs = packsQuery.data ?? []
  const active = packs.find((p) => p.vocabulary_active)?.pack ?? null

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <ModalContent size="sm" showClose={false}>
        <ModalHeader>
          <ModalTitle>Deployment vocabulary</ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Which pack names the records staff see — the sidebar entry, page title and
            "New …" button. Changes at once; installing a pack does not change it.
          </p>
          {packsQuery.isLoading && (
            <p className="text-sm text-muted-foreground">Loading starter packs…</p>
          )}
          {packsQuery.isError && (
            <p className="text-sm text-destructive">Couldn't load starter packs.</p>
          )}
          {packs.length > 0 && (
            <fieldset
              role="radiogroup"
              aria-label="Deployment vocabulary"
              className="space-y-2"
              disabled={setVocabulary.isPending}
            >
              <legend className="sr-only">Deployment vocabulary</legend>
              {packs.map((pack) => (
                <label
                  key={pack.pack}
                  className="flex items-center gap-2 text-sm text-foreground cursor-pointer"
                >
                  <input
                    type="radio"
                    name="deployment-vocabulary"
                    value={pack.pack}
                    checked={active === pack.pack}
                    onChange={() => setVocabulary.mutate(pack.pack)}
                  />
                  <span>
                    {localized(pack.name)}
                    {pack.record_noun && (
                      <span className="text-muted-foreground"> — {pack.record_noun}</span>
                    )}
                  </span>
                </label>
              ))}
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input
                  type="radio"
                  name="deployment-vocabulary"
                  value=""
                  checked={active === null}
                  onChange={() => setVocabulary.mutate(null)}
                />
                <span>None (Records)</span>
              </label>
            </fieldset>
          )}
          {setVocabulary.isError && (
            <p className="text-xs text-destructive">
              Couldn't change the deployment vocabulary — try again.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
